import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  Statistic,
  Typography,
  Modal,
  Descriptions,
  Timeline,
  message
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
  CarOutlined,
  UserOutlined
} from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Order {
  id: string;
  type: 'rental' | 'ride';
  customer: {
    name: string;
    phone: string;
  };
  vehicle: {
    brand: string;
    model: string;
    plateNumber: string;
  };
  driver?: {
    name: string;
    phone: string;
  };
  status: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  amount: number;
  startTime: string;
  endTime?: string;
  pickupLocation: string;
  dropoffLocation?: string;
  duration?: number; // 時間
  distance?: number; // キロメートル
  createdAt: string;
  notes?: string;
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dateRange: null as any,
    keyword: ''
  });

  // サンプルデータ
  useEffect(() => {
    setOrders([
      {
        id: 'O20231201001',
        type: 'rental',
        customer: {
          name: '田中太郎',
          phone: '090-1234-5678'
        },
        vehicle: {
          brand: 'メルセデス・ベンツ',
          model: 'E300',
          plateNumber: '品川330あ1234'
        },
        status: 'active',
        amount: 45000,
        startTime: '2023-12-01 09:00',
        endTime: '2023-12-01 18:00',
        pickupLocation: '羽田空港第3ターミナル',
        dropoffLocation: '東京駅丸の内南口',
        duration: 9,
        createdAt: '2023-12-01 08:30',
        notes: 'チャイルドシートをご用意ください'
      },
      {
        id: 'O20231201002',
        type: 'ride',
        customer: {
          name: '佐藤花子',
          phone: '080-9876-5432'
        },
        vehicle: {
          brand: 'トヨタ',
          model: 'プリウス',
          plateNumber: '品川500と9012'
        },
        driver: {
          name: '鈴木運転手',
          phone: '070-5555-1234'
        },
        status: 'completed',
        amount: 8500,
        startTime: '2023-12-01 14:15',
        endTime: '2023-12-01 15:00',
        pickupLocation: '新宿駅南口',
        dropoffLocation: '六本木ヒルズ',
        duration: 0.75,
        distance: 25,
        createdAt: '2023-12-01 14:10'
      },
      {
        id: 'O20231201003',
        type: 'rental',
        customer: {
          name: '山田一郎',
          phone: '090-8888-7777'
        },
        vehicle: {
          brand: 'BMW',
          model: 'X5',
          plateNumber: '品川300さ5678'
        },
        status: 'pending',
        amount: 68000,
        startTime: '2023-12-02 10:00',
        endTime: '2023-12-02 18:00',
        pickupLocation: '品川駅高輪口',
        dropoffLocation: '品川駅高輪口',
        duration: 8,
        createdAt: '2023-12-01 13:45'
      }
    ]);
  }, []);

  const statusColors = {
    pending: 'orange',
    confirmed: 'blue',
    active: 'processing',
    completed: 'success',
    cancelled: 'error'
  };

  const statusLabels = {
    pending: '承認待ち',
    confirmed: '予約確定',
    active: 'サービス中',
    completed: 'ご利用完了',
    cancelled: 'キャンセル'
  };

  const typeColors = {
    rental: 'blue',
    ride: 'green'
  };

  const typeLabels = {
    rental: 'レンタカー',
    ride: 'タクシー'
  };

  const columns = [
    {
      title: 'ご注文番号',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => <span style={{ fontFamily: 'monospace' }}>{id}</span>
    },
    {
      title: 'サービス種別',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: keyof typeof typeLabels) => (
        <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
      )
    },
    {
      title: 'お客様',
      key: 'customer',
      width: 120,
      render: (_: any, record: Order) => (
        <div>
          <div>{record.customer.name}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.customer.phone}</div>
        </div>
      )
    },
    {
      title: '車両',
      key: 'vehicle',
      width: 140,
      render: (_: any, record: Order) => (
        <div>
          <div>{record.vehicle.brand} {record.vehicle.model}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.vehicle.plateNumber}</div>
        </div>
      )
    },
    {
      title: '運転手',
      key: 'driver',
      width: 100,
      render: (_: any, record: Order) => (
        record.driver ? (
          <div>
            <div>{record.driver.name}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>{record.driver.phone}</div>
          </div>
        ) : 'なし'
      )
    },
    {
      title: '状況',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: keyof typeof statusLabels) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      )
    },
    {
      title: '料金',
      dataIndex: 'amount',
      key: 'amount',
      width: 80,
      render: (amount: number) => `¥${amount}`
    },
    {
      title: '開始時刻',
      dataIndex: 'startTime',
      key: 'startTime',
      width: 140
    },
    {
      title: '出発地',
      dataIndex: 'pickupLocation',
      key: 'pickupLocation',
      width: 150,
      ellipsis: true
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: Order) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            詳細
          </Button>
          {record.status === 'pending' && (
            <Button
              type="link"
              onClick={() => handleConfirm(record.id)}
            >
              承認
            </Button>
          )}
        </Space>
      )
    }
  ];

  const handleViewDetail = (order: Order) => {
    setSelectedOrder(order);
    setDetailModalVisible(true);
  };

  const handleConfirm = (orderId: string) => {
    setOrders(prev => prev.map(order => 
      order.id === orderId 
        ? { ...order, status: 'confirmed' }
        : order
    ));
    message.success('ご注文を承認しました');
  };

  // フィルタリングロジック
  const filteredOrders = orders.filter(order => {
    if (filters.status && order.status !== filters.status) return false;
    if (filters.type && order.type !== filters.type) return false;
    if (filters.keyword) {
      const keyword = filters.keyword.toLowerCase();
      return (
        order.id.toLowerCase().includes(keyword) ||
        order.customer.name.toLowerCase().includes(keyword) ||
        order.customer.phone.includes(keyword) ||
        order.vehicle.plateNumber.toLowerCase().includes(keyword)
      );
    }
    return true;
  });

  const totalStats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    activeOrders: orders.filter(o => o.status === 'active').length,
    completedOrders: orders.filter(o => o.status === 'completed').length,
    totalRevenue: orders.filter(o => o.status === 'completed').reduce((sum, o) => sum + o.amount, 0)
  };

  const getStatusTimeline = (order: Order) => {
    const items = [
      {
        color: 'green',
        children: `ご注文作成 - ${order.createdAt}`
      }
    ];

    if (['confirmed', 'active', 'completed'].includes(order.status)) {
      items.push({
        color: 'blue',
        children: 'ご注文承認'
      });
    }

    if (['active', 'completed'].includes(order.status)) {
      items.push({
        color: 'orange',
        children: `サービス開始 - ${order.startTime}`
      });
    }

    if (order.status === 'completed') {
      items.push({
        color: 'green',
        children: `サービス完了 - ${order.endTime}`
      });
    }

    if (order.status === 'cancelled') {
      items.push({
        color: 'red',
        children: 'ご注文キャンセル'
      });
    }

    return items;
  };

  return (
    <div>
      <Title level={2}>ご注文管理</Title>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="ご注文総数"
              value={totalStats.totalOrders}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="承認待ち"
              value={totalStats.pendingOrders}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="サービス中"
              value={totalStats.activeOrders}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="総売上"
              value={totalStats.totalRevenue}
              prefix={<DollarOutlined />}
              formatter={(value) => `¥${Number(value).toLocaleString()}`}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* フィルターエリア */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              placeholder="ご注文状況"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="pending">承認待ち</Option>
              <Option value="confirmed">予約確定</Option>
              <Option value="active">サービス中</Option>
              <Option value="completed">ご利用完了</Option>
              <Option value="cancelled">キャンセル</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="サービス種別"
              style={{ width: '100%' }}
              allowClear
              value={filters.type}
              onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
            >
              <Option value="rental">レンタカー</Option>
              <Option value="ride">タクシー</Option>
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['開始日', '終了日']}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
            />
          </Col>
          <Col span={6}>
            <Input
              placeholder="注文番号、お客様名、電話番号、車両番号で検索"
              prefix={<SearchOutlined />}
              value={filters.keyword}
              onChange={(e) => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            />
          </Col>
        </Row>
      </Card>

      {/* ご注文リスト */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredOrders}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            total: filteredOrders.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]}件 / 全${total}件`
          }}
        />
      </Card>

      {/* ご注文詳細モーダル */}
      <Modal
        title="ご注文詳細"
        open={detailModalVisible}
        footer={null}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Descriptions title={`ご注文 ${selectedOrder.id}`} bordered column={2}>
              <Descriptions.Item label="サービス種別">
                <Tag color={typeColors[selectedOrder.type]}>
                  {typeLabels[selectedOrder.type]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="ご注文状況">
                <Tag color={statusColors[selectedOrder.status]}>
                  {statusLabels[selectedOrder.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="お客様お名前">{selectedOrder.customer.name}</Descriptions.Item>
              <Descriptions.Item label="お電話番号">{selectedOrder.customer.phone}</Descriptions.Item>
              <Descriptions.Item label="車両情報">
                {selectedOrder.vehicle.brand} {selectedOrder.vehicle.model}
              </Descriptions.Item>
              <Descriptions.Item label="車両番号">{selectedOrder.vehicle.plateNumber}</Descriptions.Item>
              {selectedOrder.driver && (
                <>
                  <Descriptions.Item label="運転手お名前">{selectedOrder.driver.name}</Descriptions.Item>
                  <Descriptions.Item label="運転手電話">{selectedOrder.driver.phone}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="開始時刻">{selectedOrder.startTime}</Descriptions.Item>
              <Descriptions.Item label="終了時刻">{selectedOrder.endTime || 'サービス中'}</Descriptions.Item>
              <Descriptions.Item label="出発地" span={2}>{selectedOrder.pickupLocation}</Descriptions.Item>
              {selectedOrder.dropoffLocation && (
                <Descriptions.Item label="目的地" span={2}>{selectedOrder.dropoffLocation}</Descriptions.Item>
              )}
              {selectedOrder.duration && (
                <Descriptions.Item label="サービス時間">{selectedOrder.duration}時間</Descriptions.Item>
              )}
              {selectedOrder.distance && (
                <Descriptions.Item label="走行距離">{selectedOrder.distance}km</Descriptions.Item>
              )}
              <Descriptions.Item label="ご利用料金">¥{selectedOrder.amount}</Descriptions.Item>
              <Descriptions.Item label="作成日時">{selectedOrder.createdAt}</Descriptions.Item>
              {selectedOrder.notes && (
                <Descriptions.Item label="備考" span={2}>{selectedOrder.notes}</Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ marginTop: 24 }}>
              <Title level={4}>ご注文進行状況</Title>
              <Timeline items={getStatusTimeline(selectedOrder)} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderManagement;